/**
 * Google Analytics 4 (GA4) Tracking Utility
 * Tracks page views only on public pages
 */

/**
 * List of public page paths where Google Analytics should track
 */
const PUBLIC_PAGES = [
  '/',
  '/pricing',
  '/features',
  '/faqs',
  '/about',
  '/blog',
  '/prosperia-ai',
  '/pwa-welcome',
  '/privacy',
  '/terms',
  '/eula',
  '/cookies',
  // Feature detail pages
  '/features/estimates-invoices',
  '/features/client-management',
  '/features/job-tracking',
  '/features/employee-management',
  '/features/mobile-app',
  '/features/payment-processing',
  '/features/embeddable-widgets',
  '/features/scheduling-calendar',
  '/features/automations',
  '/features/file-management',
  '/features/ai-powered-tools',
  '/features/site-analysis-tools',
  '/features/services-packages',
  '/features/expense-management',
  '/features/reports-analytics',
  '/features/tool-belt'
]

/**
 * Check if the current path is a public page
 * @param {string} pathname - Current pathname
 * @returns {boolean}
 */
export const isPublicPage = (pathname) => {
  // Check exact matches
  if (PUBLIC_PAGES.includes(pathname)) {
    return true
  }

  // Check if it's a public route (starts with /public/)
  if (pathname.startsWith('/public/')) {
    return true
  }

  // Check if it's a payment success page
  if (pathname.startsWith('/payment/success')) {
    return true
  }

  // Check if it's email verification page
  if (pathname.startsWith('/email-verification')) {
    return true
  }

  // Check if it's reset password page
  if (pathname.startsWith('/reset-password')) {
    return true
  }

  return false
}

/**
 * Track a page view on Google Analytics
 * Only tracks if on a public page
 * @param {string} pathname - Current pathname
 * @param {string} title - Page title (optional)
 */
export const trackGoogleAnalyticsPageView = (pathname, title = document.title) => {
  // Only track on public pages
  if (!isPublicPage(pathname)) {
    return
  }

  // Check if Google Analytics is loaded
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', 'page_view', {
        page_path: pathname,
        page_title: title,
        page_location: window.location.href
      })
      console.log('[GA4] Page view tracked:', pathname)
    } catch (error) {
      console.error('Error tracking Google Analytics page view:', error)
    }
  } else {
    console.warn('[GA4] gtag not loaded yet')
  }
}

/**
 * Track a custom event on Google Analytics
 * Only tracks if on a public page
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters (optional)
 */
export const trackGoogleAnalyticsEvent = (eventName, eventParams = {}) => {
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : ''

  // Only track on public pages
  if (!isPublicPage(currentPath)) {
    return
  }

  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    try {
      window.gtag('event', eventName, eventParams)
      console.log('[GA4] Event tracked:', eventName, eventParams)
    } catch (error) {
      console.error('Error tracking Google Analytics event:', error)
    }
  }
}
